import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { HandHeart, MapPin, PauseCircle, PlayCircle, XCircle, CheckCircle2, ExternalLink, MailCheck } from "lucide-react";

const STATUS_CLS = {
  active: "bg-emerald-50 text-emerald-800 border-emerald-200",
  paused: "bg-amber-50 text-amber-800 border-amber-300",
  stopped: "bg-slate-100 text-slate-700 border-slate-300",
};

export default function WitnessVolunteer() {
  const { volunteerToken } = useParams();
  const [view, setView] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    api.get(`/public/witness-network/volunteer/${volunteerToken}`)
      .then((r) => setView(r.data))
      .catch(() => setError("This volunteer link was not found."));
  }, [volunteerToken]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (status) => {
    try {
      await api.patch(`/public/witness-network/volunteer/${volunteerToken}`, { status });
      toast.success(status === "active" ? "You are active again" : status === "paused" ? "Requests paused" : "You have left the Witness Network");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update");
    }
  };

  const act = async (kind, requestId) => {
    try {
      const r = await api.post(`/public/witness-network/offer/${volunteerToken}/${kind}`, { request_id: requestId });
      if (kind === "accept") {
        toast.success("Request accepted — open the witness link to sign");
        if (r.data.witness_url) window.location.href = r.data.witness_url;
      } else {
        toast.success("Request declined");
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  if (error) {
    return <div className="wyv-container py-16"><Card className="max-w-lg"><CardContent className="p-6 text-sm text-muted-foreground">{error}</CardContent></Card></div>;
  }
  if (!view) {
    return <div className="wyv-container py-16 max-w-2xl space-y-4"><Skeleton className="h-24" /><Skeleton className="h-48" /></div>;
  }

  return (
    <div className="wyv-container py-10 sm:py-14 max-w-2xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading font-bold text-2xl sm:text-3xl text-foreground inline-flex items-center gap-2" data-testid="volunteer-page-title">
            <HandHeart className="h-6 w-6 text-accent" /> Witness Network — My Page
          </h1>
          <p className="text-sm text-muted-foreground mt-1 inline-flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5" /> {view.municipality}{view.ward ? ` · ${view.ward}` : ""} · {view.completed_count} witnessed
          </p>
        </div>
        <Badge variant="outline" className={`${STATUS_CLS[view.status] || STATUS_CLS.stopped} text-sm px-3 py-1`} data-testid="volunteer-status-badge">
          {view.status === "active" ? "Active" : view.status === "paused" ? "Paused" : "Stopped"}
        </Badge>
      </div>

      {view.verification_required && (
        <Card className="mt-5 border-amber-200 bg-amber-50/60" data-testid="volunteer-verification-card">
          <CardContent className="p-5">
            <p className="text-sm text-amber-900 flex items-start gap-2">
              <MailCheck className="h-4 w-4 mt-0.5 shrink-0" />
              Confirm your email ({view.identity?.email_masked}) to receive and accept witness requests.
              Check your inbox for the verification link.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Offers */}
      <Card className="mt-5" data-testid="volunteer-offers-card">
        <CardContent className="p-6">
          <h2 className="font-heading font-semibold text-base text-foreground mb-3">Witness requests for you</h2>
          {view.offers.length === 0 && (
            <p className="text-sm text-muted-foreground" data-testid="no-offers-note">
              No requests right now. When someone in your area needs a witness, the request appears here.
            </p>
          )}
          <div className="space-y-3">
            {view.offers.map((o) => (
              <div key={o.request_id} className="rounded-lg border border-blue-200 bg-blue-50/40 p-4" data-testid={`offer-${o.request_id}`}>
                <p className="text-sm text-foreground">
                  <span className="font-semibold">Witness request</span> — someone in{" "}
                  <span className="font-semibold">{o.municipality}{o.ward ? ` — ${o.ward}` : ""}</span>{" "}
                  needs a witness for their WitnessYourVote declaration.
                </p>
                <p className="text-[11px] text-muted-foreground mt-1">{o.election} · offer expires {new Date(o.expires_at).toLocaleDateString("en-CA")}</p>
                <div className="flex gap-2.5 mt-3">
                  <Button size="sm" className="bg-accent hover:bg-accent/90" onClick={() => act("accept", o.request_id)} data-testid={`accept-offer-${o.request_id}`}>
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> ACCEPT REQUEST
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => act("decline", o.request_id)} data-testid={`decline-offer-${o.request_id}`}>
                    DECLINE
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {view.assignments.length > 0 && (
            <div className="mt-6">
              <h3 className="font-heading font-semibold text-sm text-foreground mb-2">Accepted — ready to witness</h3>
              {view.assignments.map((a) => (
                <div key={a.request_id} className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-4 mb-2" data-testid={`assignment-${a.request_id}`}>
                  <p className="text-sm text-foreground">{a.municipality}{a.ward ? ` — ${a.ward}` : ""}</p>
                  <Button asChild size="sm" className="mt-2" data-testid={`open-witness-${a.request_id}`}>
                    <a href={a.witness_url}>Open Witness Page <ExternalLink className="h-3.5 w-3.5 ml-1.5" /></a>
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Controls */}
      <Card className="mt-5">
        <CardContent className="p-6">
          <h2 className="font-heading font-semibold text-sm text-foreground mb-3">My availability</h2>
          <div className="flex flex-wrap gap-2.5">
            {view.status !== "active" && (
              <Button size="sm" onClick={() => setStatus("active")} data-testid="volunteer-activate">
                <PlayCircle className="h-4 w-4 mr-1.5" /> Resume requests
              </Button>
            )}
            {view.status === "active" && (
              <Button size="sm" variant="outline" onClick={() => setStatus("paused")} data-testid="volunteer-pause">
                <PauseCircle className="h-4 w-4 mr-1.5" /> Pause requests
              </Button>
            )}
            {view.status !== "stopped" && (
              <Button size="sm" variant="ghost" className="text-rose-700 hover:text-rose-800 hover:bg-rose-50" onClick={() => setStatus("stopped")} data-testid="volunteer-stop">
                <XCircle className="h-4 w-4 mr-1.5" /> Leave the Witness Network
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <p className="text-[11px] text-muted-foreground leading-relaxed mt-6" data-testid="volunteer-safety-note">{view.safety_note}</p>
    </div>
  );
}
